import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { NgxUiLoaderService } from 'ngx-ui-loader';
import { catchError, map, Observable, of, Subject, tap, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
  buildEmissionOnboardingPayload,
  buildEmissionOnboardingPayloadFromDetail,
  normalizeEmissionOnboardingApiRow,
} from 'src/app/shared/helpers/emission-onboarding-payload.util';
import { EmissionReportResultsComponent } from '../../components/generate-emission-report/emission-report-results/emission-report-results.component';
import {
  normalizeEmissionDetailRow,
  normalizeEmissionVesselList,
} from 'src/app/modules/PC_modules/onboarding-vessels/onboarding-vessel-mapper.util';

@Injectable({
  providedIn: 'root'
})
export class EmissionService {

  private readonly baseUrl = environment.apiBasePcUrl;
  /** Apps API — /api/emission/vessel-onboarding */
  private readonly appsBaseUrl = environment.apiBaseUrl;
  private readonly onboardingContextKey = 'emission-onboarding-context';
  private onboardingVesselContext: Record<string, unknown> | null = null;
  private readonly vesselsRefresh$ = new Subject<void>();

  /** Emits when emission vessel onboarding list should be reloaded. */
  readonly emissionVesselsRefresh$ = this.vesselsRefresh$.asObservable();

  notifyEmissionVesselsChanged(): void {
    this.vesselsRefresh$.next();
  }

  constructor(
    private http: HttpClient,
    private dialog: MatDialog,
    private toastr: ToastrService,
    private loader: NgxUiLoaderService
  ) { }



  getCIITableData(queryParams: any = {}): Observable<any[]> {
    const url = `${this.baseUrl}/emission/vessels`;
    return this.http
      .get<any[]>(url, { params: queryParams })
      .pipe(catchError(this.errorHandler));
  }

  getVesselsHistoricalData(): Observable<any> {
    const url = `${this.baseUrl}/emission/vessels-historical`;
    return this.http
      .get<any>(url)
      .pipe(catchError(this.errorHandler));
  }

  getImoDcsData(queryParams: any = {}): Observable<any> {
    const url = `${this.baseUrl}/emission/imo-dcs`;
    return this.http
      .get<any>(url, { params: queryParams })
      .pipe(catchError(this.errorHandler));
  }

  getFuelULShipViewData(vesselIMO: string): Observable<any[]> {
    const url = `${this.baseUrl}/eu_ets/${vesselIMO}`;
    return this.http
      .get<any[]>(url)
      .pipe(catchError(this.errorHandler));
  }

  getETSVessels(queryParams: any = {}): Observable<any> {
    const url = `${this.baseUrl}/emission/ets-vessels`;
    return this.http
      .get<any>(url, { params: queryParams })
      .pipe(catchError(this.errorHandler));
  }

  getETSVoyages(imo: number | string): Observable<any> {
    const url = `${this.baseUrl}/emission/ets-voyages/${imo}`;
    return this.http
      .get<any>(url)
      .pipe(catchError(this.errorHandler));
  }

  getCIITableDataByIMO(imo: number | string): Observable<any> {
    const url = `${this.baseUrl}/emission/voyages/${imo}`;
    return this.http
      .get<any>(url)
      .pipe(catchError(this.errorHandler));
  }
  getCiiReport(imo: number, payload: any): Observable<any> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'X-Skip-Ngx-Ui-Loader': 'true'
    });

    return this.http.post(
      `${this.baseUrl}/emission/cii-report/${imo}`,
      payload,
      { headers }
    );
  }

  getImoDcsDetailedReport(imo: number, payload: any): Observable<any> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'X-Skip-Ngx-Ui-Loader': 'true'
    });

    return this.http.post(
      `${this.baseUrl}/emission/imo-dcs-detailed/${imo}`,
      payload,
      { headers }
    );
  }

  getFuelEuReport(imo: number, payload: any): Observable<any> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'X-Skip-Ngx-Ui-Loader': 'true'
    });

    return this.http.post(
      `${this.baseUrl}/v1/fueleu/generate-report/${imo}`,
      payload,
      { headers }
    );
  }

  getEuEtsReport(imo: number, payload: any): Observable<any> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'X-Skip-Ngx-Ui-Loader': 'true'
    });

    return this.http.post(
      `${this.baseUrl}/v1/eu-ets/generate-report/${imo}`,
      payload,
      { headers }
    );
  }

  getUkMrvReport(imo: number, payload: any): Observable<any> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'X-Skip-Ngx-Ui-Loader': 'true'
    });

    return this.http.post(
      `${this.baseUrl}/v1/uk-mrv/generate-report/${imo}`,
      payload,
      { headers }
    );
  }

  showReportResults(res: any, reportType?: string) {
    this.dialog.open(EmissionReportResultsComponent, {
      width: '500px',
      data: { ...res, reportType },
      disableClose: false,
      panelClass: 'meraki-dialog-container'
    });
  }

  generateCiiReportInBackground(imo: number, payload: any) {
    this.loader.stopAll();

    this.getCiiReport(imo, payload).subscribe({
      next: (res: any) => {
        if (res?.success === true) {
          this.showReportResults(res);
        } else {
          this.toastr.warning('C CII Report generation failed');
        }
      },
      error: (err) => {
        console.error('Report Error:', err);
        this.toastr.error('Failed to generate CII report', 'Error');
      },
    });
  }

  getDocumentListByIMO(
    imo: number,
    docType?: 'BDN' | 'LOP',
    includeDetails: boolean = false
  ): Observable<any> {

    let url = `${this.baseUrl}/emission/doc-list/${imo}`;
    const params: any = {};

    if (docType) {
      params.doc_type = docType;
    }
    if (includeDetails) {
      params.include_details = true;
    }

    return this.http.get(url, { params })
      .pipe(catchError(this.errorHandler));
  }
  uploadBDN(file: File): Observable<any> {
    const formData = new FormData();
    formData.append('file', file);

    return this.http.post(
      `${this.baseUrl}/emission/upload-bdn`,
      formData
    ).pipe(catchError(this.errorHandler));
  }

  getBdnList(imo: number): Observable<any> {
    return this.http
      .get(`${this.baseUrl}/emission/doc-list/${imo}`, {
        params: { doc_type: 'BDN' }
      })
      .pipe(catchError(this.errorHandler));
  }


  uploadLOP(bdnId: number, file: File): Observable<any> {
    const formData = new FormData();
    formData.append('file', file);

    return this.http
      .post(
        `${this.baseUrl}/emission/upload-lop/${bdnId}`,
        formData
      )
      .pipe(catchError(this.errorHandler));
  }

  manualUploadBDN(file: File, data: any): Observable<any> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('data', JSON.stringify(data));

    return this.http.post(
      `${this.baseUrl}/emission/manual-upload-bdn`,
      formData
    ).pipe(catchError(this.errorHandler));
  }

  manualUploadLOP(bdnId: number, file: File, data: any): Observable<any> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('data', JSON.stringify(data));

    return this.http.post(
      `${this.baseUrl}/emission/manual-upload-lop/${bdnId}`,
      formData
    ).pipe(catchError(this.errorHandler));
  }
  verifyBDN(docId: number, payload: any): Observable<any> {
    return this.http.post(
      `${this.baseUrl}/emission/verify-bdn/${docId}`,
      payload
    ).pipe(catchError(this.errorHandler));
  }
  verifyLOP(docId: number, payload: any): Observable<any> {
    return this.http.post(
      `${this.baseUrl}/emission/verify-lop/${docId}`,
      payload
    ).pipe(catchError(this.errorHandler));
  }


  printFinalReport(payload: any): Observable<Blob> {
    const url = `${this.baseUrl}/emission/print_final_report`;
    return this.http
      .post(url, payload, { responseType: 'blob' })
      .pipe(catchError(this.errorHandler));
  }

  /* ---------------- DASHBOARD APIS ---------------- */
  
  getDashboardStats(client: string = 'ALL'): Observable<any> {
    const url = `${this.baseUrl}/v1/emission/dashboard-stats`;
    let params = new HttpParams();
    if (client !== 'ALL') {
      params = params.set('client', client);
    }
    return this.http.get<any>(url, { params }).pipe(catchError(this.errorHandler));
  }

  getCiiPredictiveAllowance(imo: string | number, months: number = 3, buffer: number = 0.03): Observable<any> {
    const url = `${this.baseUrl}/v1/emission/cii-predictive-allowance`;
    let params = new HttpParams()
      .set('imo', imo.toString())
      .set('months', months.toString())
      .set('buffer', buffer.toString());
    return this.http.get<any>(url, { params }).pipe(catchError(this.errorHandler));
  }


  getCiiBenchmarking(client: string = 'ALL'): Observable<any> {
    const url = `${this.baseUrl}/v1/emission/cii-benchmarking`;
    let params = new HttpParams();
    if (client !== 'ALL') {
      params = params.set('client', client);
    }
    return this.http.get<any>(url, { params }).pipe(catchError(this.errorHandler));
  }

  getIntensityBenchmarking(client: string = 'ALL'): Observable<any> {
    const url = `${this.baseUrl}/v1/emission/intensity-benchmarking`;
    let params = new HttpParams();
    if (client !== 'ALL') {
      params = params.set('client', client);
    }
    return this.http.get<any>(url, { params }).pipe(catchError(this.errorHandler));
  }

  getAreaDistribution(client: string = 'ALL'): Observable<any> {
    const url = `${this.baseUrl}/v1/emission/area-distribution`;
    let params = new HttpParams();
    if (client !== 'ALL') {
      params = params.set('client', client);
    }
    return this.http.get<any>(url, { params }).pipe(catchError(this.errorHandler));
  }

  getFleetMiniView(client: string = 'ALL'): Observable<any> {
    const url = `${this.baseUrl}/v1/emission/fleet-mini-view`;
    let params = new HttpParams();
    if (client !== 'ALL') {
      params = params.set('client', client);
    }
    return this.http.get<any>(url, { params }).pipe(catchError(this.errorHandler));
  }

  /* ---------------- EMISSION VESSEL ONBOARDING (apps API) ---------------- */

  setOnboardingVesselContext(context: Record<string, unknown>): void {
    this.onboardingVesselContext = context;
    if (context?.['imo']) {
      sessionStorage.setItem(
        this.onboardingContextKey,
        JSON.stringify(context),
      );
    }
  }

  getOnboardingVesselContext(): Record<string, unknown> | null {
    if (this.onboardingVesselContext) {
      return this.onboardingVesselContext;
    }
    const stored = sessionStorage.getItem(this.onboardingContextKey);
    if (!stored) {
      return null;
    }
    try {
      this.onboardingVesselContext = JSON.parse(stored);
      return this.onboardingVesselContext;
    } catch {
      return null;
    }
  }

  /**
   * POST /api/emission/vessel-onboarding?session={session}
   * Body: application/json — emission onboarding contract.
   */
  private emissionVesselOnboardingUrl(session?: string): string {
    const resolvedSession = session ?? this.getSession();
    return `${this.appsBaseUrl}/api/emission/vessel-onboarding?session=${encodeURIComponent(resolvedSession)}`;
  }

  /** Map UI module keys to backend onboarding_modules values. */
  mapOnboardingModules(modules: string[]): string[] {
    return [...new Set(modules.map((module) => (module === 'speed' ? 'sailing' : module)))];
  }

  buildEmissionVesselOnboardingPayload(
    config: Record<string, unknown>,
    options?: { original?: Record<string, unknown> | null; applyFactorDefaults?: boolean },
  ): Record<string, unknown> {
    if (config['vessel'] && config['onboarding_form'] && config['emission']) {
      const modules = this.mapOnboardingModules(
        Array.isArray(config['onboarding_modules'])
          ? (config['onboarding_modules'] as string[])
          : [],
      ).filter((module) => module === 'emission' || module === 'sailing');

      return buildEmissionOnboardingPayload({
        submittedAt: config['submitted_at'] as string,
        onboardingModules: modules.length ? modules : ['emission'],
        quickOnboarding: config['quick_onboarding'] === true,
        vessel: config['vessel'] as Record<string, unknown>,
        onboardingForm: config['onboarding_form'] as Record<string, unknown>,
        emission: config['emission'] as Record<string, unknown>,
        original:
          options?.original ??
          (config['__original'] as Record<string, unknown> | null | undefined),
        applyFactorDefaults: options?.applyFactorDefaults,
      });
    }

    return buildEmissionOnboardingPayloadFromDetail(config, options?.original);
  }

  postEmissionVesselOnboardingRegistration(
    payload: Record<string, unknown>,
    options?: { original?: Record<string, unknown> | null; applyFactorDefaults?: boolean },
  ): Observable<unknown> {
    const body = this.buildEmissionVesselOnboardingPayload(payload, options);
    const url = this.emissionVesselOnboardingUrl();
    return this.http
      .post(url, body, {
        headers: { 'Content-Type': 'application/json' },
      })
      .pipe(
        tap(() => this.notifyEmissionVesselsChanged()),
        catchError(this.errorHandler),
      );
  }

  getEmissionVesselOnboardingVessels(): Observable<Record<string, unknown>[]> {
    return this.http
      .get<unknown>(this.emissionVesselOnboardingUrl())
      .pipe(
        map((data) => normalizeEmissionVesselList(data)),
        catchError((error: HttpErrorResponse) => {
          console.error('Failed to load emission onboarded vessels:', error);
          return of([]);
        }),
      );
  }

  getEmissionVesselOnboardingDetails(
    vesselImo: string,
  ): Observable<Record<string, unknown> | null> {
    const session = this.getSession();
    const url = `${this.appsBaseUrl}/api/emission/vessel-onboarding?vessel_imo=${encodeURIComponent(vesselImo)}&session=${encodeURIComponent(session)}`;
    return this.http.get<unknown>(url).pipe(
      map((data) => {
        const normalized = normalizeEmissionDetailRow(data);
        return Object.keys(normalized).length ? normalized : null;
      }),
      catchError((error: HttpErrorResponse) => {
        if (error.status === 404) {
          return of(null);
        }
        console.error('Failed to load emission vessel details:', error);
        return of(null);
      }),
    );
  }

  getSession(): string {
    const username = localStorage.getItem('username') || '';
    if (!username || this.isMerakiOperations(username)) {
      return 'admin';
    }
    const sessionByUser: Record<string, string> = {
      'Bunge Shipping': 'Bunge SA',
      'Elite Shipping': 'Elite Tankships',
      'Eiger Shipping': 'Northwell',
      'Zeronorth ZN': 'zeronorth',
      'Alcor Shipping': 'alcor',
      'EMZoil Shipping': 'emz',
      'Ethiopian Shipping & Logistics': 'ethiopian',
    };
    if (sessionByUser[username]) {
      return sessionByUser[username];
    }
    if (username.toLowerCase().includes('bunge')) {
      return 'Bunge SA';
    }
    return username;
  }

  private isMerakiOperations(username: string): boolean {
    const normalized = username.trim().toLowerCase();
    return (
      normalized === 'meraki operations' ||
      normalized === 'merakioperations' ||
      normalized.includes('meraki')
    );
  }

  private errorHandler(error: HttpErrorResponse) {
    console.error('Emission API Error:', error);
    return throwError(() => error);
  }
}
